import React, { useState, useMemo } from 'react';
import {
  Badge, Button, EmptyState, SearchInput, Select, SkeletonRows, Sym, initialsOf,
} from '../../../components/app2';
import { getStrapiImageUrl } from '../../../utils/strapiHelpers';

/**
 * Listado maestro de empresas (rediseño jul-2026).
 *
 * Sigue el acordeón del diseño: cada fila muestra la empresa con su estado y,
 * al abrirla, tres bloques —información básica, contacto y estado SAT—. Se
 * conservan la búsqueda, el filtro por estado, el orden y las TRES acciones
 * (administradores, editar, ver empresa), que en el diseño solo eran dos.
 *
 * El estado de la e.firma sube a la fila: el propio inventario señala que es el
 * dato más accionable de esta pantalla y antes había que expandir para verlo.
 */

const STATUS = {
  activa: { tone: 'ok', icon: 'check_circle', label: 'Activa' },
  suspendida: { tone: 'warn', icon: 'pause_circle', label: 'Suspendida' },
  inactiva: { tone: 'neutral', icon: 'cancel', label: 'Inactiva' },
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'activa', label: 'Activas' },
  { value: 'inactiva', label: 'Inactivas' },
  { value: 'suspendida', label: 'Suspendidas' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'created', label: 'Más recientes' },
  { value: 'created_desc', label: 'Más antiguas' },
  { value: 'employees', label: 'Más empleados' },
  { value: 'admins', label: 'Más administradores' },
];

/** Traduce el estado de la FIEL a un badge. Sin dato, no se inventa. */
const fielBadge = (fielStatus) => {
  const v = String(fielStatus || '').toLowerCase();
  if (!v || v === 'unknown' || v === 'desconocido') return { tone: 'neutral', label: 'FIEL sin verificar' };
  if (v.includes('vál') || v.includes('valid') || v.includes('vigente') || v === 'ok') return { tone: 'ok', label: 'FIEL vigente' };
  if (v.includes('vencer') || v.includes('pronto') || v.includes('warn')) return { tone: 'warn', label: 'FIEL por vencer' };
  if (v.includes('venc') || v.includes('expir') || v.includes('error') || v.includes('inval')) return { tone: 'danger', label: 'FIEL vencida' };
  return { tone: 'neutral', label: `FIEL ${fielStatus}` };
};

const Dato = ({ icon, label, children }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
    <Sym name={icon} size={16} style={{ color: 'var(--faint)', flexShrink: 0, marginTop: 2 }} />
    <div style={{ minWidth: 0 }}>
      <div style={{ font: "500 .74rem 'Hanken Grotesk'", color: 'var(--muted)' }}>{label}</div>
      <div style={{ font: "600 .9rem 'Hanken Grotesk'", color: 'var(--heading)', wordBreak: 'break-word' }}>
        {children || 'No especificado'}
      </div>
    </div>
  </div>
);

const Bloque = ({ title, children }) => (
  <div style={{ background: 'var(--surface)', padding: '18px 20px' }}>
    <div
      className="a2-mono"
      style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--faint)', marginBottom: 12 }}
    >
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>{children}</div>
  </div>
);

const CompanyList = ({
  companies,
  onSelectCompany,
  onCreateNew,
  onViewCompany,
  onViewAdministrators,
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [expandedCompany, setExpandedCompany] = useState(null);

  const filteredAndSortedCompanies = useMemo(() => {
    const filtered = (companies || []).filter((company) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        company?.businessName?.toLowerCase()?.includes(q) ||
        company?.rfc?.toLowerCase()?.includes(q);
      const matchesStatus = statusFilter === 'all' || company?.state === statusFilter;
      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name': return (a?.businessName || '').localeCompare(b?.businessName || '');
        case 'name_desc': return (b?.businessName || '').localeCompare(a?.businessName || '');
        case 'created': return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
        case 'created_desc': return new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0);
        case 'employees': return (b?.employeeCount || 0) - (a?.employeeCount || 0);
        case 'admins': return (b?.adminCount || 0) - (a?.adminCount || 0);
        default: return 0;
      }
    });
  }, [companies, searchTerm, statusFilter, sortBy]);

  const toggleAccordion = (companyId) =>
    setExpandedCompany(expandedCompany === companyId ? null : companyId);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  if (isLoading) return <SkeletonRows rows={6} title={false} />;

  const hayFiltros = Boolean(searchTerm) || statusFilter !== 'all';

  return (
    <div>
      {/* Buscador, filtros y alta */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          placeholder="Buscar por nombre o RFC…"
          style={{ flex: 1, minWidth: 220 }}
          aria-label="Buscar empresa por nombre o RFC"
        />
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e?.target?.value)}
          aria-label="Filtrar por estado"
          style={{ width: 'auto', minWidth: 165, padding: '11px 14px', fontSize: '.9rem', background: 'var(--surface)' }}
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e?.target?.value)}
          aria-label="Ordenar empresas"
          style={{ width: 'auto', minWidth: 175, padding: '11px 14px', fontSize: '.9rem', background: 'var(--surface)' }}
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
        <Button icon="add" onClick={onCreateNew}>Nueva empresa</Button>
      </div>

      {filteredAndSortedCompanies.length === 0 ? (
        <EmptyState
          icon={hayFiltros ? 'domain_disabled' : 'domain_add'}
          title={hayFiltros ? 'Sin resultados' : 'No hay empresas registradas'}
          text={hayFiltros
            ? 'Ajusta la búsqueda o los filtros.'
            : 'Comienza dando de alta tu primera empresa cliente.'}
          action={!hayFiltros ? <Button icon="add" onClick={onCreateNew}>Nueva empresa</Button> : null}
        />
      ) : (
        // gap 5 + fila '8px 18px': densidad pedida en el ticket 86e2h7yq2 (la
        // separación original dispersaba el listado). SOLO esta pantalla.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {filteredAndSortedCompanies.map((company) => {
            const abierta = expandedCompany === company?.id;
            const estado = STATUS[company?.state] || STATUS.inactiva;
            const fiel = fielBadge(company?.fielStatus);
            const logo = getStrapiImageUrl(company?.logo, 'thumbnail');

            return (
              <div
                key={company?.id}
                className="a2-card"
                style={{ overflow: 'hidden', borderColor: abierta ? 'var(--brand-line)' : undefined }}
              >
                {/* Fila (padding vertical comprimido; el horizontal se queda en
                    18px para no pegar el contenido al borde de la tarjeta) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 18px' }}>
                  <button
                    type="button"
                    onClick={() => toggleAccordion(company?.id)}
                    aria-expanded={abierta}
                    aria-label={`${abierta ? 'Contraer' : 'Expandir'} ${company?.businessName}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0,
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    {logo ? (
                      <img
                        src={logo}
                        alt=""
                        style={{ width: 44, height: 44, borderRadius: 11, objectFit: 'cover', border: '1px solid var(--line-2)', flexShrink: 0 }}
                      />
                    ) : (
                      <span
                        className="a2-avatar"
                        style={{
                          width: 44, height: 44, borderRadius: 11, fontSize: '.95rem',
                          background: 'var(--brand-soft)', border: '1px solid var(--brand-line)', color: 'var(--brand-2)',
                        }}
                      >
                        {initialsOf(company?.businessName)}
                      </span>
                    )}

                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span
                        className="a2-truncate"
                        style={{ display: 'block', font: "700 1rem 'Hanken Grotesk'", color: 'var(--heading)' }}
                        title={company?.businessName}
                      >
                        {company?.businessName}
                      </span>
                      <span
                        className="a2-mono a2-truncate"
                        style={{ display: 'block', fontSize: '.8rem', color: 'var(--muted)' }}
                      >
                        {company?.rfc}
                      </span>
                    </span>

                    <span className="a2-hide-sm" style={{ textAlign: 'right', minWidth: 76 }}>
                      <span style={{ display: 'block', font: "700 .95rem 'Hanken Grotesk'", color: 'var(--heading)' }}>
                        {company?.employeeCount || 0}
                      </span>
                      <span style={{ display: 'block', font: "500 .7rem 'Hanken Grotesk'", color: 'var(--muted)' }}>
                        empleados
                      </span>
                    </span>

                    <span className="a2-hide-sm"><Badge tone={fiel.tone}>{fiel.label}</Badge></span>
                    <Badge tone={estado.tone} icon={estado.icon}>{estado.label}</Badge>
                    <Sym name={abierta ? 'expand_less' : 'expand_more'} size={24} style={{ color: 'var(--muted)' }} />
                  </button>
                </div>

                {/* Detalle */}
                {abierta ? (
                  <div
                    className="a2-anim-fadein"
                    style={{
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))',
                      gap: 1, background: 'var(--line)', borderTop: '1px solid var(--line)',
                    }}
                  >
                    <Bloque title="Información básica">
                      <Dato icon="domain" label="Razón social">{company?.businessName}</Dato>
                      <Dato icon="tag" label="RFC">{company?.rfc}</Dato>
                      <Dato icon="location_on" label="Dirección">{company?.address}</Dato>
                    </Bloque>

                    <Bloque title="Contacto">
                      <Dato icon="mail" label="Correo">{company?.contactEmail}</Dato>
                      <Dato icon="call" label="Teléfono">{company?.contactPhone}</Dato>
                      <div style={{ display: 'flex', gap: 20 }}>
                        <Dato icon="group" label="Empleados">{company?.employeeCount || 0}</Dato>
                        <Dato icon="shield_person" label="Admins">{company?.adminCount || 0}</Dato>
                      </div>
                    </Bloque>

                    <Bloque title="Estado SAT y sistema">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Sym name="badge" size={18} style={{ color: 'var(--faint)' }} />
                        <Badge tone={fiel.tone}>{fiel.label}</Badge>
                      </div>
                      {/* El origen guarda 'connected'/'disconnected' en inglés;
                          se traduce al pintarlo, no se cambia el dato. */}
                      <Dato icon="cloud_sync" label="Conexión SAT">
                        {company?.satConnection === 'connected' ? 'Conectada'
                          : company?.satConnection === 'disconnected' ? 'Sin conectar'
                          : company?.satConnection}
                      </Dato>
                      <Dato icon="schedule" label="Última sincronización">
                        {company?.lastSync
                          ? new Date(company.lastSync).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
                          : 'Nunca'}
                      </Dato>

                      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                        <Button
                          variant="outline"
                          size="sm"
                          icon="edit"
                          onClick={(e) => { stop(e); onSelectCompany?.(company); }}
                          style={{ flex: 1, minWidth: 110 }}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          icon="bar_chart"
                          onClick={(e) => { stop(e); onViewCompany?.(company); }}
                          style={{ flex: 1, minWidth: 110 }}
                        >
                          Ver empresa
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        icon="shield_person"
                        onClick={(e) => { stop(e); onViewAdministrators?.(company); }}
                        style={{ width: '100%' }}
                      >
                        {company?.adminCount || 0} administradores
                      </Button>
                    </Bloque>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 14, padding: '0 4px' }}>
        <span style={{ font: "500 .82rem 'Hanken Grotesk'", color: 'var(--muted)' }}>
          Mostrando {filteredAndSortedCompanies.length} de {(companies || []).length} empresas
        </span>
      </div>
    </div>
  );
};

export default CompanyList;
